'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import styles from './HeroSection.module.scss';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Linkedin, Phone, Share2 } from 'lucide-react';
import { gaCta } from '@/src/helpers/gtag';

export interface HeroAction {
  type: 'phone' | 'linkedin' | 'share' | string;
  label: string;
  href: string;
  event?: string;
  shareSuccessLabel?: string;
}

export interface HeroSectionProps {
  tagline: string;
  title: string;
  highlightedTitle: string;
  description: string;
  primaryCta: string;
  primaryHref: string;
  secondaryCta?: string;
  secondaryHref?: string;
  actions?: HeroAction[];
  backgroundImage?: string;
  backgroundImageAlt?: string;
  heroImageSrc?: string;
}

const iconMap = {
  phone: Phone,
  linkedin: Linkedin,
  share: Share2,
};

export default function HeroSection({
  tagline,
  title,
  highlightedTitle,
  description,
  primaryCta,
  primaryHref,
  actions = [],
  backgroundImage,
  backgroundImageAlt = '',
  heroImageSrc,
}: HeroSectionProps) {
  const rootRef = useRef<HTMLElement | null>(null);
  const [shareTooltip, setShareTooltip] = useState<string | null>(null);
  const imageSrc = heroImageSrc || backgroundImage || '';

  useEffect(() => {
    if (!rootRef.current) {
      return;
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    const root = rootRef.current;
    const ctx = gsap.context(() => {
      /* ── Hero parallax ── */
      gsap
        .timeline({
          scrollTrigger: {
            trigger: '[data-hero]',
            start: 'top top',
            end: 'bottom top',
            scrub: 0.5,
          },
        })
        .to('[data-hero-content]', { y: -32, opacity: 0.75, duration: 0.3, ease: 'none' }, 0)
        .to('[data-hero-bg]', { y: -48, scale: 1.03, duration: 0.3, ease: 'none' }, 0);
    }, root);

    return () => ctx.revert();
  }, []);

  const handleShare = async (e: React.MouseEvent, action: HeroAction) => {
    e.preventDefault();
    const url = window.location.href;
    const shareData = { title: document.title, url };

    try {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share(shareData);
        gaCta(action.label, action.event || 'hero_click_share', 'native_share');
      } else if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        gaCta(action.label, action.event || 'hero_click_share', 'clipboard_copy');
        setShareTooltip(action.shareSuccessLabel || 'Lien copié');
        setTimeout(() => setShareTooltip(null), 2000);
      }
    } catch {
      // User cancelled or share API unavailable — keep the UI silent.
    }
  };

  const renderQuickAction = (action: HeroAction) => {
    const Icon = iconMap[action.type as keyof typeof iconMap];
    const iconEl = Icon ? <Icon size={16} strokeWidth={2} aria-hidden="true" /> : null;

    if (action.type === 'share') {
      return (
        <button
          key={action.type}
          type="button"
          className={styles.quickAction}
          onClick={(e) => handleShare(e, action)}
          aria-label={`${action.label} cette page`}
        >
          {iconEl}
          <span className={styles.quickActionLabel}>{action.label}</span>
          {shareTooltip && (
            <span className={styles.shareTooltip} role="status" aria-live="polite">
              {shareTooltip}
            </span>
          )}
        </button>
      );
    }

    const isExternal = action.href.startsWith('http');

    return (
      <a
        key={action.type}
        href={action.href}
        className={styles.quickAction}
        onClick={() =>
          gaCta(action.label, action.event || `hero_action_${action.type}`, action.href)
        }
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
      >
        {iconEl}
        <span className={styles.quickActionLabel}>{action.label}</span>
      </a>
    );
  };

  return (
    <section ref={rootRef} className={styles.sequence}>
      {/* ── Hero ── */}
      <section className={styles.hero} data-hero>
        <div className={styles.heroBg} data-hero-bg>
          <Image
            src={imageSrc}
            alt={backgroundImageAlt}
            fill
            priority
            sizes="100vw"
            className={styles.heroBgImage}
          />
        </div>

        <div className={styles.heroContent} data-hero-content>
          <p className={styles.eyebrow}>{tagline}</p>

          <h1>
            {title}
            <span>{highlightedTitle}</span>
          </h1>

          <p className={styles.heroText}>{description}</p>

          <div className={styles.heroActions}>
            <a
              href={primaryHref}
              className={styles.primaryButton}
              onClick={() => gaCta(primaryCta, 'hero_primary', primaryHref)}
            >
              {primaryCta}
              <span aria-hidden="true">→</span>
            </a>
          </div>

          {actions.length > 0 && (
            <div className={styles.heroQuickActions}>
              {actions.map((action) => renderQuickAction(action))}
            </div>
          )}
        </div>
      </section>
    </section>
  );
}
